import React, { useState, useEffect } from 'react';
import { Col, Row, Content, Text } from 'components';
import './Receipt.style.css';
import { useParams } from 'react-router-dom';
import { useGetGuestOrder } from 'hooks/OrderHooks';
import LOGO from 'assets/svg/main_logo.svg';
import Formatter from 'utils/Formatter';
import { calculateFutureDate, stringToMoneyFormat } from 'utils';

const ORDER_TITLE = {
  STATUS: {
    ko: '주문상태',
    en: 'Order Status',
  },
};

const ORDER_STATUS = {
  READY: {
    ko: '주문 확인중',
    en: 'Awaiting Confirmation',
  },
  PREPARING: {
    ko: '준비중',
    en: 'Preparing',
  },
  COMPLETED: {
    ko: '준비완료',
    en: 'Ready for Pickup',
  },
  CANCELLED: {
    ko: '주문취소',
    en: 'Cancelled',
  },
};

const Receipt = (props) => {
  /* ===== Route ===== */
  const params = useParams();

  /* ===== Data ===== */
  const { data: orderData } = useGetGuestOrder(
    {
      payment_serial_number: params?.order_id,
      join_tables: '["shop_information"]',
    },
    {
      enabled: !!params?.order_id,
      refetchInterval: 5000, // 5초마다 갱신
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return null;

        return data;
      },
      initialData: null,
    }
  );

  /* ===== Props ===== */
  /* ===== State ===== */
  // 웹뷰 여부
  const [isWebView, setIsWebView] = useState(false);
  // 쿠폰 총 할인금
  const [totalCouponDiscount, setTotalCouponDiscount] = useState(0);

  /* ===== Function ===== */
  /* ===== Variable ===== */
  const Divider = () => {
    return <div className="receipt-divider" />;
  };

  /* ===== Hooks ===== */
  /**
   * 웹뷰 감지
   * --
   */
  useEffect(() => {
    const isNative = window.ReactNativeWebView;

    setIsWebView(isNative ? true : false);
  }, []);

  /**
   * 쿠폰 총 할인금
   * --
   */
  useEffect(() => {
    if (!orderData) return;

    const { coupon_info } = orderData;

    if (coupon_info?.length) {
      const total = coupon_info.reduce(
        (acc, cur) => (acc += cur.discount_price),
        0
      );

      setTotalCouponDiscount(total);
    } else {
      setTotalCouponDiscount(0);
    }
  }, [orderData]);

  /* ===== Render ===== */
  return (
    <Content maxWidth={720}>
      <Row>
        <Col x={24}>
          <Content maxWidth={'100%'}>
            <div className="receipt-logo">
              <img
                src={LOGO}
                alt="Logo"
                style={{ width: 140, color: '#111111' }}
              />
            </div>

            {!isWebView ? (
              <div className="receipt-center">
                <Text size={16}>
                  {ORDER_TITLE?.STATUS?.ko}:{' '}
                  {ORDER_STATUS[orderData?.order_status]?.ko}
                </Text>
                <Text size={16}>
                  {ORDER_TITLE?.STATUS?.en}:{' '}
                  {ORDER_STATUS[orderData?.order_status]?.en}
                </Text>
              </div>
            ) : null}

            <Divider />

            {/* 매장 정보 */}
            <div className="receipt-space-between">
              <Text className="receipt-shop-info" size={13} color={'#666666'}>
                {orderData?.shop_information?.shop_information_name ?? '-'}
              </Text>
              <Text className="receipt-shop-info" size={13} color={'#666666'}>
                T:{' '}
                {orderData?.shop_information?.store_information?.store_num ??
                  '-'}
              </Text>
            </div>
            <div className="receipt-space-between">
              <Text className="receipt-shop-info" size={13} color={'#666666'}>
                {orderData?.shop_information?.shop_information_address ?? '-'}
              </Text>
              <Text className="receipt-shop-info" size={13} color={'#666666'}>
                Date:{' '}
                {Formatter.unixToDate(
                  orderData?.created_at,
                  'YYYY-MM-DD HH:mm'
                ) ?? '-'}
              </Text>
            </div>
            {/* <div className="receipt-space-between">
              <Text className="receipt-shop-info" size={13} color={'#666666'}>
                대표자:{' '}
                {orderData?.shop_information?.store_information
                  ?.store_owner ?? '-'}
              </Text>
            </div> */}
            <div className="receipt-space-between">
              <Text className="receipt-shop-info" size={13} color={'#666666'}>
                주문번호: {orderData?.payment_serial_number ?? '-'}
              </Text>
            </div>
            {orderData?.order_route === 'KIOSK' ? (
              <div className="receipt-space-between">
                <Text className="receipt-shop-info" size={13} color={'#666666'}>
                  결제 승인번호:{' '}
                  {orderData?.payment_info?.payment_auth_number ?? '-'}
                </Text>
              </div>
            ) : null}

            <Divider />

            {/* 주문 번호 */}
            <div className="receipt-center">
              <Text size={19} color={'#484848'} weight={600}>
                주문번호: {orderData?.order_number}
              </Text>
            </div>

            <Divider />

            {/* 주문 상품 */}
            <div>
              {orderData?.product_information?.map((_item, _index) => (
                <div key={`order_${_index}`}>
                  <div className="receipt-space-between">
                    <Text
                      className="receipt-menu"
                      style={{ flex: 1 }}
                      size={14}
                      color={'#666666'}
                    >
                      {_item?.menu_name}{' '}
                      {_item?.temperature_option === 'OTHER'
                        ? ''
                        : `(${_item?.temperature_option})`}
                    </Text>

                    <div className="receipt-menu-info">
                      <Text
                        className="receipt-menu"
                        size={14}
                        color={'#666666'}
                      >
                        {stringToMoneyFormat(_item?.price)}
                      </Text>
                      <Text
                        className="receipt-menu"
                        size={14}
                        color={'#666666'}
                      >
                        {stringToMoneyFormat(_item?.num)}
                      </Text>
                      <Text
                        className="receipt-menu"
                        size={14}
                        color={'#666666'}
                      >
                        {stringToMoneyFormat(_item?.num * _item?.price)}
                      </Text>
                    </div>
                  </div>
                  {_item?.menu_option?.map((_subItem, _subIndex) =>
                    _subItem?.option?.map((_thirdItem, _thirdIndex) => (
                      <div
                        key={`${_index}_option_${_subIndex}`}
                        className="receipt-space-between"
                      >
                        <Text
                          className="receipt-menu"
                          style={{ flex: 1 }}
                          size={14}
                          color={'#666666'}
                        >
                          ᄂ{_subItem?.option_title} ({_thirdItem?.option_name})
                        </Text>

                        <div className="receipt-menu-info">
                          <Text
                            className="receipt-menu"
                            size={14}
                            color={'#666666'}
                          >
                            {stringToMoneyFormat(_thirdItem?.option_price)}
                          </Text>
                          <Text
                            className="receipt-menu"
                            size={14}
                            color={'#666666'}
                          >
                            {stringToMoneyFormat(
                              _thirdItem?.count === 0 ? 1 : _thirdItem?.count
                            )}
                          </Text>
                          <Text
                            className="receipt-menu"
                            size={14}
                            color={'#666666'}
                          >
                            {stringToMoneyFormat(
                              (_thirdItem?.count === 0
                                ? 1
                                : _thirdItem?.count) * _thirdItem?.option_price
                            )}
                          </Text>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ))}
              <div className="receipt-space-between" style={{ marginTop: 4 }}>
                <Text
                  className="receipt-menu"
                  style={{ flex: 1 }}
                  size={14}
                  color={'#666666'}
                  weight={600}
                >
                  합계
                </Text>

                <div className="receipt-menu-info">
                  <Text className="receipt-menu"></Text>
                  <Text
                    className="receipt-menu"
                    size={14}
                    color={'#666666'}
                    weight={600}
                  >
                    {stringToMoneyFormat(
                      orderData?.product_information?.length
                    )}
                  </Text>
                  <Text
                    className="receipt-menu"
                    size={14}
                    color={'#666666'}
                    weight={600}
                  >
                    {stringToMoneyFormat(
                      orderData?.final_price - orderData?.final_discount
                    )}
                  </Text>
                </div>
              </div>
            </div>

            {/* 할인/포인트/쿠폰 */}
            {orderData?.use_point ||
            orderData?.coupon_info?.length ||
            totalCouponDiscount + orderData?.use_point >
              orderData?.final_discount ? (
              <React.Fragment>
                <Divider />

                <div>
                  {totalCouponDiscount + orderData?.use_point !==
                  orderData?.final_discount ? (
                    <div className="receipt-space-between">
                      <Text
                        className="receipt-menu"
                        style={{ flex: 1 }}
                        size={14}
                        color={'#666666'}
                      >
                        등급할인
                      </Text>

                      <div className="receipt-menu-info">
                        <p className="receipt-menu"></p>
                        <p className="receipt-menu"></p>
                        <Text
                          className="receipt-menu"
                          size={14}
                          color={'#666666'}
                        >
                          {stringToMoneyFormat(
                            orderData?.final_discount -
                              (totalCouponDiscount + orderData?.use_point)
                          )}
                        </Text>
                      </div>
                    </div>
                  ) : null}

                  {!orderData?.coupon_info?.length
                    ? null
                    : orderData?.coupon_info?.map((_item, _index) => (
                        <div
                          key={`coupon_${_index}`}
                          className="receipt-space-between"
                        >
                          <Text
                            className="receipt-menu"
                            style={{ flex: 1 }}
                            size={14}
                            color={'#666666'}
                          >
                            {_item?.coupon_name}
                          </Text>

                          <div className="receipt-menu-info">
                            <p className="receipt-menu"></p>
                            <p className="receipt-menu"></p>
                            <Text
                              className="receipt-menu"
                              size={14}
                              color={'#666666'}
                            >
                              {stringToMoneyFormat(_item?.discount_price)}
                            </Text>
                          </div>
                        </div>
                      ))}

                  {orderData?.use_point ? (
                    <div className="receipt-space-between">
                      <Text
                        className="receipt-menu"
                        style={{ flex: 1 }}
                        size={14}
                        color={'#666666'}
                      >
                        사용 포인트
                      </Text>

                      <div className="receipt-menu-info">
                        <p className="receipt-menu"></p>
                        <p className="receipt-menu"></p>
                        <Text
                          className="receipt-menu"
                          size={14}
                          color={'#666666'}
                        >
                          {stringToMoneyFormat(orderData?.use_point)}
                        </Text>
                      </div>
                    </div>
                  ) : null}
                </div>
              </React.Fragment>
            ) : null}

            <Divider />

            {/* 결제금액 */}
            <div className="receipt-space-between">
              <Text
                size={19}
                color={'#484848'}
                weight={600}
                style={{ flex: 1 }}
              >
                결제금액<span style={{ fontSize: 14 }}>(VAT포함)</span>
              </Text>

              <div className="receipt-menu-info">
                <Text
                  size={19}
                  color={'#484848'}
                  weight={600}
                  style={{ flex: 1, textAlign: 'right' }}
                >
                  {stringToMoneyFormat(orderData?.final_price)}
                </Text>
              </div>
            </div>

            {/* 현금영수증 */}
            {orderData?.cash_bill_info && orderData?.is_cash_bill === 'Y' ? (
              <React.Fragment>
                <Divider />
                <div className="receipt-space-between">
                  <Text size={14} color={'#484848'} style={{ flex: 1 }}>
                    현금영수증 승인번호
                  </Text>

                  <div className="receipt-menu-info">
                    <Text
                      size={14}
                      color={'#484848'}
                      style={{ flex: 1, textAlign: 'right' }}
                    >
                      {orderData?.cash_bill_info?.cashbillNumb}
                    </Text>
                  </div>
                </div>
                <div className="receipt-space-between">
                  <Text size={14} color={'#484848'} style={{ flex: 1 }}>
                    현금영수증 승인금액
                  </Text>

                  <div className="receipt-menu-info">
                    <Text
                      size={14}
                      color={'#484848'}
                      style={{ flex: 1, textAlign: 'right' }}
                    >
                      {stringToMoneyFormat(
                        orderData?.cash_bill_info?.totalAmount
                      )}
                    </Text>
                  </div>
                </div>
                <div className="receipt-space-between">
                  <Text size={14} color={'#484848'} style={{ flex: 1 }}>
                    현금영수증 승인일시
                  </Text>

                  <div className="receipt-menu-info">
                    <Text
                      size={14}
                      color={'#484848'}
                      style={{ flex: 1, textAlign: 'right' }}
                    >
                      {orderData?.cash_bill_info?.tradeDateTime?.replace(
                        /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/,
                        '$1.$2.$3 $4:$5:$6'
                      )}
                    </Text>
                  </div>
                </div>
              </React.Fragment>
            ) : null}

            {/* WIFI 정보 */}
            {orderData?.shop_information?.wifi_information ? (
              <React.Fragment>
                <Divider />

                <div className="receipt-space-between">
                  <p
                    className="receipt-menu"
                    style={{ whiteSpace: 'pre-wrap', fontSize: 15 }}
                  >
                    {orderData?.shop_information?.wifi_information}
                  </p>
                </div>
              </React.Fragment>
            ) : null}

            {/* 화장실 정보 */}
            {orderData?.shop_information?.toilet_information ? (
              <React.Fragment>
                <Divider />

                <div className="receipt-space-between">
                  <p
                    className="receipt-menu"
                    style={{ whiteSpace: 'pre-wrap', fontSize: 15 }}
                  >
                    {orderData?.shop_information?.toilet_information}
                  </p>
                </div>
              </React.Fragment>
            ) : null}

            <Divider />

            <div className="receipt-center">
              <p className="receipt-menu" style={{ textAlign: 'center' }}>
                결제수단 변경 또는 환불은 구입하신 매자에서 가능하며,
                <br />
                반드시 영수증을 함께 지참하여 30일 이내 방문하셔야 합니다.
                <br />
                [가능기간:{' '}
                {Formatter.unixToDate(
                  calculateFutureDate(orderData?.payment_completion_date, 30),
                  'YYYY-MM-DD'
                )}
                ]
              </p>

              {!isWebView ? (
                <p className="receipt-menu" style={{ textAlign: 'center' }}>
                  For payment changes or refunds,
                  <br />
                  please visit the store where you made the purchase within 30
                  days with this receipt.
                  <br />
                  [Request deadline:{' '}
                  {Formatter.unixToDate(
                    calculateFutureDate(orderData?.payment_completion_date, 30),
                    'MMMM DD, YYYY',
                    'en'
                  )}
                  ]
                </p>
              ) : null}
            </div>
          </Content>
        </Col>
      </Row>
    </Content>
  );
};

export default Receipt;
